import React from "react";

interface SplashProps {
  onStartSubscription?: () => void;
  onExistingUserLogin?: () => void;
  onBookDemo?: () => void;
  isSigningIn?: boolean;
}

export default function Splash({
  onStartSubscription,
  onExistingUserLogin,
  onBookDemo,
  isSigningIn = false,
}: SplashProps) {
  const features = [
    {
      title: "Projects & Bidding",
      description:
        "Build projects, manage quotes, organise shots and assets, and keep production data structured from the first brief to final delivery.",
    },
    {
      title: "Documents & AI Extraction",
      description:
        "Upload production documents, analyse them with AI, and turn useful information into structured project data faster.",
    },
    {
      title: "People & Operations",
      description:
        "Manage staff, roles, access, and operational information in one connected workspace built for busy teams.",
    },
    {
      title: "Meetings & Scheduling",
      description:
        "Coordinate project meetings, calendars, and collaboration so conversations stay tied to the work that matters.",
    },
    {
      title: "CRM & Relationships",
      description:
        "Keep track of clients, companies, contacts, and project relationships without switching between disconnected tools.",
    },
    {
      title: "Google Workspace Connected",
      description:
        "Bring together Google sign-in, Drive workflows, shared files, and team collaboration in a familiar ecosystem.",
    },
  ];

  const benefits = [
    "One platform for bidding, production admin, people, documents, and collaboration",
    "Structured project data instead of scattered folders, emails, and spreadsheets",
    "AI-assisted workflows that reduce repetitive manual entry",
    "Role-based access for teams, managers, and administrators",
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <section className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900">
        <div className="absolute inset-0 opacity-50">
          <div className="absolute left-[-10%] top-[-15%] h-72 w-72 rounded-full bg-slate-200 dark:bg-slate-800 blur-3xl" />
          <div className="absolute right-[-5%] top-24 h-80 w-80 rounded-full bg-slate-300 dark:bg-slate-700 blur-3xl" />
          <div className="absolute bottom-[-10%] left-1/3 h-64 w-64 rounded-full bg-slate-200 dark:bg-slate-800 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 pt-6 lg:px-10">
          <div className="flex justify-end">
            <button
              onClick={onExistingUserLogin}
              disabled={isSigningIn}
              className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-5 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
            >
              Existing User Login
            </button>
          </div>
        </div>

        <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-14 lg:grid-cols-2 lg:px-10 lg:py-24">
          <div className="flex flex-col justify-center">
            <div className="mb-6 inline-flex w-fit items-center rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium shadow-sm">
              Production workflow, bidding, operations, and people in one app
            </div>

            <h1 className="max-w-3xl font-brandon text-5xl font-medium tracking-tight text-slate-950 dark:text-slate-50 sm:text-6xl">
              Run your production workflow from a single intelligent workspace.
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
              Bidding Tools brings together projects, quotes, shots, assets,
              documents, meetings, CRM, and people management into one clean
              system designed to replace scattered admin and disconnected tools.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={onStartSubscription}
                disabled={isSigningIn}
                className="rounded-lg bg-slate-950 dark:bg-slate-100 px-6 py-3 text-sm font-medium text-white dark:text-slate-950 shadow-lg transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSigningIn ? "Signing In..." : "Start Subscription"}
              </button>
              <button
                onClick={onBookDemo}
                className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-6 py-3 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm transition hover:-translate-y-0.5"
              >
                Book a Demo
              </button>
            </div>

            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {benefits.map((benefit) => (
                <div
                  key={benefit}
                  className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 p-4 shadow-sm backdrop-blur"
                >
                  <p className="text-sm leading-6 text-slate-700 dark:text-slate-300">{benefit}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="w-full max-w-2xl rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-2xl shadow-slate-300/40 dark:shadow-black/30">
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950/60 p-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow-sm border border-transparent dark:border-slate-800">
                    <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                      Dashboard
                    </div>
                    <div className="mt-4 text-3xl font-semibold">24</div>
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Active projects
                    </div>
                  </div>
                  <div className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow-sm border border-transparent dark:border-slate-800">
                    <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                      Quotes
                    </div>
                    <div className="mt-4 text-3xl font-semibold">118</div>
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Structured bid items
                    </div>
                  </div>
                  <div className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow-sm border border-transparent dark:border-slate-800">
                    <div className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                      Team
                    </div>
                    <div className="mt-4 text-3xl font-semibold">42</div>
                    <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Managed users
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-[1.35fr_0.95fr]">
                  <div className="rounded-lg bg-white dark:bg-slate-900 p-5 shadow-sm border border-transparent dark:border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          Project workflow overview
                        </h3>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          Quotes, shots, assets, documents, and exports linked together.
                        </p>
                      </div>
                      <div className="rounded-md bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium text-slate-600 dark:text-slate-300">
                        Live workspace
                      </div>
                    </div>

                    <div className="mt-6 space-y-3">
                      {[
                        ["Quote imported", "Complete"],
                        ["Shots created", "128 items"],
                        ["Assets linked", "57 items"],
                        ["Documents analysed", "12 files"],
                        ["Meeting notes synced", "Updated"],
                      ].map(([label, status]) => (
                        <div
                          key={label}
                          className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3"
                        >
                          <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
                          <span className="text-sm font-medium text-slate-950 dark:text-slate-100">{status}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg bg-white dark:bg-slate-900 p-5 shadow-sm border border-transparent dark:border-slate-800">
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      Connected modules
                    </h3>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {[
                        "Projects",
                        "Quotes",
                        "People",
                        "CRM",
                        "Calendar",
                        "Documents",
                        "Exports",
                        "Permissions",
                      ].map((item) => (
                        <span
                          key={item}
                          className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300"
                        >
                          {item}
                        </span>
                      ))}
                    </div>

                    <div className="mt-6 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 p-4">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        Built for operational clarity
                      </div>
                      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        Give your team a cleaner way to organise work, reduce admin,
                        and keep every project moving with better visibility.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="mx-auto max-w-3xl text-center">
          <div className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400">
            Everything connected
          </div>
          <h2 className="mt-4 font-brandon text-4xl font-medium tracking-tight text-slate-950 dark:text-slate-50">
            Designed for modern production teams that need more than a spreadsheet.
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-600 dark:text-slate-300">
            Replace manual admin with a connected operating system for bidding,
            production coordination, file handling, and team management.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
            >
              <div className="h-11 w-11 rounded-md bg-slate-900 dark:bg-slate-100" />
              <h3 className="mt-5 text-xl font-semibold text-slate-950 dark:text-slate-50">
                {feature.title}
              </h3>
              <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-2 lg:px-10">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-400">
              Why teams buy
            </div>
            <h2 className="mt-4 font-brandon text-4xl font-medium tracking-tight">
              Less admin. Better visibility. Faster decisions.
            </h2>
          </div>
          <div className="grid gap-4">
            {[
              "Keep quotes, projects, documents, and communication aligned",
              "Create consistent operational processes across teams",
              "Reduce duplicated entry with connected data and AI support",
              "Give managers and admins clearer oversight of people and work",
            ].map((item) => (
              <div
                key={item}
                className="rounded-lg border border-white/10 bg-white/5 p-5 text-sm leading-7 text-slate-200"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20 text-center lg:px-10">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-10 shadow-sm">
          <div className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">
            Ready to see it in action?
          </div>
          <h2 className="mt-4 font-brandon text-4xl font-medium tracking-tight text-slate-950">
            Start using Bidding Tools as your production operating system.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-600">
            Bring together the operational parts of your workflow into one app your team will actually want to use.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button
              onClick={onStartSubscription}
              disabled={isSigningIn}
              className="rounded-lg bg-slate-950 px-6 py-3 text-sm font-medium text-white shadow-lg transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSigningIn ? "Signing In..." : "Start Subscription"}
            </button>
            <button
              onClick={onBookDemo}
              className="rounded-lg border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:-translate-y-0.5"
            >
              Request a Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
